import { logger } from "@/modules/logger";
import { transformNoteWithVoice } from "@/actions/transformNoteWithVoice";
import { DefaultUploadManager } from "@/store/audio-capture/processing/upload/upload-manager";
import type { TranscriptArrayItem } from "@/store/audio-capture/transcription/utils/types";
import type { AppStoreAudioCaptureStore } from "@/store/audio-capture/AppStoreAudioCaptureStore";

export interface RecordingUploadPayload {
  audioBlob: Blob;
  capturedAudioId: string;
  transcriptText: string;
  transcriptArray: TranscriptArrayItem[];
  preferredMimeType?: string;
}

export interface TransformAudioArgs {
  noteId: string;
  capturedAudioId: string;
  sessionId?: string;
}

export class ProcessingManager {
  constructor(private readonly store: AppStoreAudioCaptureStore) {}

  async uploadRecording(
    payload: RecordingUploadPayload
  ): Promise<{ success: true } | { success: false; error: string }> {
    const mimeType =
      payload.audioBlob.type || payload.preferredMimeType || this.store.supportedMimeType;
    logger.info({
      message: "[AppStoreAudioCaptureStore] uploadRecording",
      info: {
        size: payload.audioBlob.size,
        mimeType: mimeType ?? null,
        transcriptLength: payload.transcriptArray.length,
      },
    });
    this.store.updateUploadProgress(0);

    this.store.uploadManager = new DefaultUploadManager({
      minUploadDurationMs: 1000,
      progressUpdateIntervalMs: 50,
    });

    let lastLoggedThreshold = -25;

    const result = await this.store.uploadManager.start(
      payload.audioBlob,
      {
        capturedAudioId: payload.capturedAudioId,
        transcriptText: payload.transcriptText,
        transcriptArray: payload.transcriptArray,
        preferredMimeType: mimeType,
      },
      {
        onProgress: (percent: number) => {
          this.store.updateUploadProgress(percent);

          const threshold = Math.floor(percent / 25) * 25;
          if (threshold >= 0 && threshold <= 100 && threshold >= lastLoggedThreshold + 25) {
            lastLoggedThreshold = threshold;
            logger.info({
              message: "[AppStoreAudioCaptureStore] upload progress",
              info: {
                capturedAudioId: payload.capturedAudioId,
                percent: threshold,
              },
            });
          }
        },
      }
    );

    if (result.success) {
      logger.info({
        message: "[AppStoreAudioCaptureStore] uploadRecording completed",
        info: {
          capturedAudioId: payload.capturedAudioId,
          size: payload.audioBlob.size,
        },
      });
    }

    this.store.updateUploadProgress(null);
    this.store.uploadManager = null;
    return result;
  }

  async transformAudioToNote(
    args: TransformAudioArgs
  ): Promise<{ success: boolean; error?: string }> {
    logger.debug({ message: "[AppStoreAudioCaptureStore] transformAudioToNote" });
    try {
      const { noteId, capturedAudioId, sessionId } = args;
      const { success, error } = await transformNoteWithVoice({
        noteId,
        capturedAudioId,
        sessionId,
        store: this.store.store,
      });
      return { success, error };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to transform audio to note";
      logger.error({
        message: "[AppStoreAudioCaptureStore][_transformAudioToNote] Transform Error",
        info: {
          error: errorMessage,
          noteId: args.noteId,
          capturedAudioId: args.capturedAudioId,
        },
      });
      return { success: false, error: errorMessage };
    }
  }
}
